import React, { memo } from 'react';
import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  type TableColumnDefinition,
  createTableColumn,
  TableCellLayout,
  Button,
  Badge,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Card,
} from '@fluentui/react-components';
import {
  DesktopRegular,
  PlugDisconnectedRegular,
  ArrowRightRegular,
  ArrowClockwiseRegular,
  InfoRegular,
} from '@fluentui/react-icons';
import { useSessions, useRefreshSessions } from '@/hooks/useSessionQueries';
import { SessionState } from '@/types/api';
import type { Session } from '@/types/api';
import { useNavigate } from '@tanstack/react-router';
import { formatDateTimeShort } from '@/utils/dateFormatters';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
  },
  infoCard: {
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `4px solid ${tokens.colorBrandBackground}`,
  },
  infoHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  infoContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  bulletList: {
    marginLeft: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalXS,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  grid: {
    minHeight: '400px',
  },
  statusBadge: {
    minWidth: '100px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: tokens.spacingVerticalM,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: tokens.spacingVerticalM,
  },
});

const getStatusColor = (state: SessionState): "success" | "warning" | "danger" | "important" => {
  switch (state) {
    case SessionState.Active:
      return "success";
    case SessionState.Disconnected:
    case SessionState.UserDisconnected:
      return "warning";
    case SessionState.LogOff:
      return "danger";
    default:
      return "important";
  }
};

/**
 * SessionList component displays a data grid of the current user's AVD sessions
 * with sorting, navigation, and refresh capabilities.
 *
 * **Why use React.memo:**
 * This component is wrapped with React.memo to prevent unnecessary re-renders when
 * parent components update. Since this component renders a data grid that can contain
 * many rows and performs complex rendering logic, memoization improves performance by
 * ensuring it only re-renders when its data changes (from React Query updates).
 * The component has no props, so it will only re-render when session data updates.
 */
const SessionListComponent: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { data: sessions, isLoading, error, refetch } = useSessions();
  const refreshMutation = useRefreshSessions();

  const columns: TableColumnDefinition<Session>[] = [
    createTableColumn<Session>({
      columnId: 'hostPool',
      renderHeaderCell: () => 'Host Pool',
      renderCell: (item) => (
        <TableCellLayout media={<DesktopRegular />}>
          {item.hostPoolName}
        </TableCellLayout>
      ),
    }),
    createTableColumn<Session>({
      columnId: 'sessionHost',
      renderHeaderCell: () => 'Session Host',
      renderCell: (item) => item.sessionHostName,
    }),
    createTableColumn<Session>({
      columnId: 'status',
      renderHeaderCell: () => 'Status',
      renderCell: (item) => (
        <Badge
          appearance="filled"
          color={getStatusColor(item.sessionState)}
          className={styles.statusBadge}
        >
          {item.sessionState}
        </Badge>
      ),
    }),
    createTableColumn<Session>({
      columnId: 'startTime',
      renderHeaderCell: () => 'Start Time',
      renderCell: (item) => formatDateTimeShort(item.startTime),
    }),
    createTableColumn<Session>({
      columnId: 'lastActivity',
      renderHeaderCell: () => 'Last Activity',
      renderCell: (item) => formatDateTimeShort(item.lastActivityTime),
    }),
    createTableColumn<Session>({
      columnId: 'actions',
      renderHeaderCell: () => 'Actions',
      renderCell: (item) => (
        <Button
          appearance="subtle"
          icon={<ArrowRightRegular />}
          onClick={() => navigate({ to: '/sessions/$sessionId', params: { sessionId: item.id } })}
        >
          View Details
        </Button>
      ),
    }),
  ];

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Spinner size="large" />
          <Text size={400}>Scanning AVD hosts for your sessions...</Text>
          <Text size={200}>This may take a few seconds</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <PlugDisconnectedRegular style={{ fontSize: '48px', color: tokens.colorPaletteRedForeground1 }} />
          <Text size={500} weight="semibold">Failed to load sessions</Text>
          <Text size={300}>{error.message}</Text>
          <Button appearance="primary" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <DesktopRegular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
          <Text size={500} weight="semibold">No Active Sessions</Text>
          <Text size={300}>You don't have any active AVD sessions at the moment.</Text>
          <Button 
            appearance="primary" 
            icon={<ArrowClockwiseRegular />}
            disabled={refreshMutation.isPending}
            onClick={() => refreshMutation.mutate()}
          >
            {refreshMutation.isPending ? 'Scanning hosts...' : 'Refresh'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card className={styles.infoCard}>
        <div className={styles.infoHeader}>
          <InfoRegular style={{ fontSize: '20px', color: tokens.colorBrandForeground1 }} />
          <Text size={600} weight="semibold">About AVD Self-Service Portal</Text>
        </div>
        <div className={styles.infoContent}>
          <Text size={400}>
            This portal allows you to manage your Azure Virtual Desktop sessions independently,
            without requiring IT support for common tasks.
          </Text>
          <div>
            <Text size={400} weight="semibold">When to use this tool:</Text>
            <ul className={styles.bulletList}>
              <li>
                <Text size={300}>
                  <strong>Disconnect stuck sessions:</strong> If your remote desktop is frozen or unresponsive
                </Text>
              </li>
              <li>
                <Text size={300}>
                  <strong>Log off properly:</strong> When you need to fully sign out and release your session
                </Text>
              </li>
              <li>
                <Text size={300}>
                  <strong>Monitor active sessions:</strong> View all your current AVD connections across different host pools
                </Text>
              </li>
              <li>
                <Text size={300}>
                  <strong>Free up resources:</strong> End sessions you're no longer using to ensure optimal performance
                </Text>
              </li>
            </ul>
          </div>
          <Text size={300} style={{ fontStyle: 'italic', color: tokens.colorNeutralForeground3 }}>
            Note: Session changes may take a few seconds to reflect. Use the Refresh button to update the list.
          </Text>
        </div>
      </Card>

      <div className={styles.header}>
        <div>
          <Text size={700} weight="semibold">Your AVD Sessions</Text>
          <br />
          <Text size={300}>Manage and monitor your Azure Virtual Desktop sessions</Text>
        </div>
        <Button
          appearance="primary"
          icon={<ArrowClockwiseRegular />}
          disabled={refreshMutation.isPending}
          onClick={() => refreshMutation.mutate()}
        >
          {refreshMutation.isPending ? 'Scanning hosts...' : 'Refresh'}
        </Button>
      </div>

      <DataGrid
        items={sessions}
        columns={columns}
        sortable
        getRowId={(item) => item.id}
        className={styles.grid}
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<Session>>
          {({ item, rowId }) => (
            <DataGridRow<Session> key={rowId}>
              {({ renderCell }) => (
                <DataGridCell>{renderCell(item)}</DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </div>
  );
};

/**
 * Memoized version of SessionList for performance optimization.
 * Export this component instead of the base component.
 */
export const SessionList = memo(SessionListComponent);